import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { InkFlourish, InkPaloma } from "../components/Ink";

const CATEGORIES = ["Gastronomía", "Cultura", "Bienestar", "Privado", "Otra"];

export default function Propose() {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    organization: "",
    event_name: "",
    event_date: "",
    category: "",
    link: "",
    why_fits: "",
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/submissions", form);
      setSent(true);
    } catch {
      toast.error("No se pudo enviar la propuesta. Intente de nuevo.");
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-6 sm:px-10 py-24 text-center" data-testid="propose-success">
        <InkFlourish className="w-32 h-4 mx-auto opacity-70" />
        <p className="eyebrow mt-6">Recibido</p>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-cobalt-deep leading-tight">
          Gracias. <span className="serif-italic text-cobalt">Lo leeremos con calma.</span>
        </h1>
        <p className="mt-6 text-muted2 leading-relaxed">
          Si encaja con la edición, le escribiremos directamente a los pocos días. Si no, agradecemos su propuesta y la guardaremos para una próxima curaduría.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="propose-page">
      {/* Header — cream canvas */}
      <section className="canvas-cream py-20 sm:py-24 relative overflow-hidden">
        <InkPaloma className="hidden md:block absolute top-14 right-20 w-40 h-24 opacity-60" stroke="#1F3A6E" />
        <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-16">
          <p className="eyebrow">Colaboraciones · Por invitación</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-cobalt-deep">
            ¿Tiene un evento que encaje en <span className="serif-italic text-cobalt">esta mesa?</span>
          </h1>
          <p className="mt-6 text-[1rem] text-muted2 leading-relaxed max-w-2xl">
            El <i>Insider</i> es una lista corta. Publicamos pocos eventos, los que sentimos que una familia del Valle pondría en su calendario sin pensarlo. No garantizamos publicación; sí garantizamos que lo leeremos.
          </p>
          <InkFlourish className="w-32 h-5 mt-8 opacity-60" />
        </div>
      </section>

      {/* Criteria — restrained */}
      <section className="bg-white max-w-3xl mx-auto px-6 sm:px-10 lg:px-16 pt-16">
        <p className="eyebrow">Lo que buscamos</p>
        <ul className="mt-5 space-y-2 text-muted2 leading-relaxed">
          <li className="flex items-start gap-3"><Check size={16} className="mt-1 text-cobalt shrink-0" /> Experiencias en cifras limitadas, bien hechas.</li>
          <li className="flex items-start gap-3"><Check size={16} className="mt-1 text-cobalt shrink-0" /> Algo que no se consigue abriendo Google.</li>
          <li className="flex items-start gap-3"><Check size={16} className="mt-1 text-cobalt shrink-0" /> Oficio real: chef, artesano, músico, anfitrión que sabe lo que hace.</li>
          <li className="flex items-start gap-3"><Check size={16} className="mt-1 text-cobalt shrink-0" /> Que mejore un fin de semana, no que lo llene.</li>
        </ul>
      </section>

      {/* Form */}
      <section className="bg-white max-w-3xl mx-auto px-6 sm:px-10 lg:px-16 py-16">
        <div className="rule-hair mb-10" />
        <form onSubmit={submit} className="space-y-8" data-testid="propose-form">
          <Field label="Su nombre">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-line" data-testid="propose-name" />
          </Field>

          <Field label="Correo o WhatsApp">
            <input required value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="input-line" placeholder="hola@ejemplo.com · +52 55 1234 5678" data-testid="propose-contact" />
          </Field>

          <Field label="Organización o proyecto (opcional)">
            <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="input-line" data-testid="propose-org" />
          </Field>

          <Field label="Nombre del evento">
            <input required value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} className="input-line" data-testid="propose-event-name" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <Field label="Fecha (si ya la tiene)">
              <input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className="input-line" data-testid="propose-event-date" />
            </Field>
            <Field label="Categoría">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-line" data-testid="propose-category">
                <option value="">Elegir…</option>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Enlace (sitio, Instagram, presentación)">
            <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://…" className="input-line" data-testid="propose-link" />
          </Field>

          <Field label="¿Por qué encaja en el Insider? Sea breve.">
            <textarea required rows={5} value={form.why_fits} onChange={(e) => setForm({ ...form, why_fits: e.target.value })} className="input-line resize-none" placeholder="Dos o tres líneas. Qué lo hace distinto." data-testid="propose-why" />
          </Field>

          <div className="pt-4 flex items-center justify-between gap-6 flex-wrap">
            <p className="text-xs text-subtle leading-relaxed max-w-md">
              Al enviar, acepta que su propuesta sea leída por la redacción. Respondemos solo si encaja.
            </p>
            <button disabled={loading} className="btn-primary disabled:opacity-50" data-testid="propose-submit">
              {loading ? "Enviando…" : "Enviar propuesta"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="eyebrow">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);
